import { SystemStyleObject } from '@telescope/cassini-ui';
import { defineStyle } from '@chakra-ui/styled-system';
import { mode, transparentize } from '@chakra-ui/theme-tools';

type AccessibleColor = {
  bg?: string;
  color?: string;
  hoverBg?: string;
  activeBg?: string;
};

/** Accessible color overrides for less accessible colors. */
const accessibleColorMap: { [key: string]: AccessibleColor } = {
  white: {
    color: 'black',
    hoverBg: 'whiteAlpha.800',
    activeBg: 'whiteAlpha.700',
  },
  black: {
    hoverBg: 'blackAlpha.800',
    activeBg: 'blackAlpha.700',
  },
  yellow: {
    bg: 'yellow.400',
    color: 'black',
    hoverBg: 'yellow.500',
    activeBg: 'yellow.600',
  },
  cyan: {
    bg: 'cyan.400',
    color: 'black',
    hoverBg: 'cyan.500',
    activeBg: 'cyan.600',
  },
};

// Same as solid button, but the background and text colors are inverted
const variantSolidInverse = defineStyle((props) => {
  const { colorScheme: c, theme } = props;
  const solidVariantProps = { ...theme.components.Button.variants.solid(props) };
  return {
    ...solidVariantProps,
    color: solidVariantProps.bg,
    bg: solidVariantProps.color,
    _hover: {
      bg: solidVariantProps.bg,
      color: solidVariantProps.color,
      _disabled: {
        bg: 'initial',
      },
    },
    _active: { bg: mode(`${c}.700`, `${c}.400`)(props) },
  };
});

const variantSolid = defineStyle((props) => {
  const { colorScheme: c } = props;

  if (c === 'gray') {
    const bg = mode(`gray.100`, `whiteAlpha.200`)(props);

    return {
      bg,
      _hover: {
        bg: mode(`gray.200`, `whiteAlpha.300`)(props),
        _disabled: {
          bg,
        },
      },
      _active: { bg: mode(`gray.300`, `whiteAlpha.400`)(props) },
    };
  }

  const { bg = `${c}.500`, color = 'white', hoverBg = `${c}.600`, activeBg = `${c}.700` } = accessibleColorMap[c] ?? {};

  const background = mode(bg, `${c}.200`)(props);

  return {
    px: 8,
    bg: background,
    color: mode(color, `gray.800`)(props),
    _hover: {
      bg: mode(hoverBg, `${c}.300`)(props),
      _disabled: {
        bg: background,
      },
    },
    _active: { bg: mode(activeBg, `${c}.400`)(props) },
  };
});

const variantGhost = defineStyle((props) => {
  const { colorScheme: c, theme } = props;

  if (c === 'gray' || c === 'white') {
    return {
      color: mode(`inherit`, `whiteAlpha.900`)(props),
      _hover: {
        bg: mode(`gray.100`, `whiteAlpha.200`)(props),
      },
      _active: { bg: mode(`gray.200`, `whiteAlpha.300`)(props) },
    };
  }

  if (c === 'black') {
    return {
      color: mode(`inherit`, `blackAlpha.900`)(props),
      _hover: {
        bg: mode(`blackAlpha.100`, `blackAlpha.200`)(props),
      },
      _active: { bg: mode(`blackAlpha.200`, `blackAlpha.300`)(props) },
    };
  }

  const {
    bg = `transparent`,
    color = `${c}.50`,
    hoverBg = `${c}.200`,
    activeBg = `${c}.100`,
  } = accessibleColorMap[c] ?? {};

  const darkHoverBg = transparentize(`${c}.200`, 0.12)(theme);
  const darkActiveBg = transparentize(`${c}.200`, 0.24)(theme);

  return {
    color: mode(color, `${c}.200`)(props),
    bg,
    _hover: {
      bg: mode(hoverBg, darkHoverBg)(props),
    },
    _active: {
      bg: mode(activeBg, darkActiveBg)(props),
    },
  };
});

export const buttonTheme = {
  baseStyle: { _disabled: { pointerEvents: 'none', bg: '#e6e6e6', color: '#7C7C7C', opacity: 1 }, fontWeight: 400 },
  variants: {
    ghost: variantGhost,
    solidInverse: variantSolidInverse,
    solid: variantSolid,
    link: ({ colorScheme }: SystemStyleObject) => ({
      _hover: { color: `${colorScheme}.300` },
    }),
  },
};
